import React from 'react';
import css from './RadioCardGroup.module.scss';

export interface RadioCardOption<T extends string> {
  value: T;
  label: React.ReactNode;
  // Optional second line under the label (renders the .text/.optHelper wrap).
  description?: React.ReactNode;
}

export interface RadioCardGroupProps<T extends string> {
  legend: React.ReactNode;
  // Legend icon element (e.g. <BellOutlined />) — the group styles it.
  icon?: React.ReactElement;
  // DOM name shared by the group's radio inputs.
  name: string;
  // Accessible name for the radiogroup; falls back to a string legend.
  ariaLabel?: string;
  options: RadioCardOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  // Kept separate from `disabled` so consumers can pass their optimistic-save
  // flag straight through; either one disables the fieldset.
  saving?: boolean;
  // Per-consumer spacing (the fieldset itself carries no margins).
  className?: string;
  // Helper/lead copy rendered between the legend and the options.
  children?: React.ReactNode;
  // Content after the options (e.g. a coaching Alert) — still inside the card.
  footer?: React.ReactNode;
}

/**
 * The settings radio-card fieldset (originated by ForwardingNotifyModeSelector):
 * a card-styled fieldset with a legend, stacked radio-card options and a custom
 * indicator with a focus ring. Purely presentational — helper copy, warnings
 * and save handlers stay with the consumers.
 */
function RadioCardGroup<T extends string>({
  legend,
  icon,
  name,
  ariaLabel,
  options,
  value,
  onChange,
  disabled,
  saving,
  className,
  children,
  footer,
}: RadioCardGroupProps<T>) {
  const groupLabel = ariaLabel ?? (typeof legend === 'string' ? legend : undefined);
  return (
    <fieldset
      className={className ? `${css.fieldset} ${className}` : css.fieldset}
      disabled={disabled || saving}
    >
      <legend className={css.legend}>
        {icon &&
          React.cloneElement(icon, { className: css.legendIcon, 'aria-hidden': 'true' })}
        {legend}
      </legend>
      {children}
      <div className={css.options} role="radiogroup" aria-label={groupLabel}>
        {options.map((opt) => {
          const checked = opt.value === value;
          return (
            <label
              key={opt.value}
              className={`${css.option} ${checked ? css.optionChecked : ''}`}
            >
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={checked}
                onChange={() => onChange(opt.value)}
                className={css.input}
              />
              <span className={css.indicator} aria-hidden="true" />
              {opt.description != null ? (
                <span className={css.text}>
                  <span className={css.optLabel}>{opt.label}</span>
                  <span className={css.optHelper}>{opt.description}</span>
                </span>
              ) : (
                <span className={css.optLabel}>{opt.label}</span>
              )}
            </label>
          );
        })}
      </div>
      {footer}
    </fieldset>
  );
}

export default RadioCardGroup;
